const API_BASE_URL = import.meta.env.VITE_API_URL;
if (!API_BASE_URL) {
  throw new Error(
    "VITE_API_URL is not set. Create .env.development with VITE_API_URL=http://localhost:8787 (or set it for your environment).",
  );
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

function handleUnauthorized(): never {
  try {
    localStorage.removeItem("auth_token");
  } catch {
    /* */
  }
  if (
    typeof window !== "undefined" &&
    !window.location.pathname.startsWith("/login")
  ) {
    window.location.assign("/login");
  }
  throw new ApiError("Unauthorized", 401);
}

async function api<T>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, headers = {} } = options;

  const configHeaders: Record<string, string> = { ...headers };

  if (
    method !== "GET" &&
    !configHeaders["Content-Type"] &&
    !(body instanceof FormData)
  ) {
    configHeaders["Content-Type"] = "application/json";
  }

  const config: RequestInit = {
    method,
    credentials: "include",
    headers: configHeaders,
  };

  try {
    const token = localStorage.getItem("auth_token");
    if (token) {
      configHeaders["Authorization"] = `Bearer ${token}`;
    }
  } catch {
    // Intentionally empty - localStorage may be unavailable in SSR
  }

  if (body !== undefined) {
    config.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  if (response.status === 401) {
    handleUnauthorized();
  }

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new ApiError(errorData.error || "Request failed", response.status);
  }

  const json = await response.json();
  return (json.data ?? json) as T;
}

export const authApi = {
  login: (email: string, password: string) =>
    api<import("../types").User & { token: string }>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    }),

  logout: () =>
    api<{ message: string }>("/api/auth/logout", {
      method: "POST",
    }),

  me: () => api<import("../types").User>("/api/auth/me"),
};

export const trainerApi = {
  getWorkoutPlans: () => api<unknown[]>("/api/trainer/plans/workout"),
  createWorkoutPlan: (payload: unknown) =>
    api<unknown>("/api/trainer/plans/workout", {
      method: "POST",
      body: payload,
    }),
  updateWorkoutPlan: (id: string, payload: unknown) =>
    api<unknown>(`/api/trainer/plans/workout/${id}`, {
      method: "PUT",
      body: payload,
    }),
  deleteWorkoutPlan: (id: string) =>
    api<void>(`/api/trainer/plans/workout/${id}`, { method: "DELETE" }),
  assignWorkoutPlan: (id: string, userIds: string[]) =>
    api<unknown>(`/api/trainer/plans/workout/${id}/assign`, {
      method: "POST",
      body: { user_ids: userIds },
    }),
  getWorkoutTemplates: () =>
    api<unknown[]>("/api/trainer/plans/workout/templates"),
  createWorkoutPlanFromTemplate: (templateId: string, payload?: unknown) =>
    api<unknown>(`/api/trainer/plans/workout/from-template/${templateId}`, {
      method: "POST",
      body: payload ?? {},
    }),
  deleteWorkoutTemplate: (id: string) =>
    api<void>(`/api/trainer/plans/workout/${id}`, { method: "DELETE" }),
  getFoodPlans: () =>
    api<{ plans: unknown[]; pagination?: unknown }>("/api/trainer/plans/food"),
  createFoodPlan: (payload: unknown) =>
    api<unknown>("/api/trainer/plans/food", {
      method: "POST",
      body: payload,
    }),
  updateFoodPlan: (id: string, payload: unknown) =>
    api<unknown>(`/api/trainer/plans/food/${id}`, {
      method: "PUT",
      body: payload,
    }),
  deleteFoodPlan: (id: string) =>
    api<void>(`/api/trainer/plans/food/${id}`, { method: "DELETE" }),
  getFoodTemplates: () => api<unknown[]>("/api/trainer/plans/food/templates"),
};

export const getTrainerUsers = (page: number = 1, limit: number = 20) =>
  api<{ users: unknown[]; pagination?: unknown }>(
    `/api/trainer/users?page=${page}&limit=${limit}`,
  );

export const postBulkAnnouncement = (payload: {
  user_ids: string[];
  content: string;
}) =>
  api<{ sent?: number }>("/api/trainer/messages/bulk", {
    method: "POST",
    body: payload,
  });

export { ApiError };
export default api;
